import { inject, Injectable } from '@angular/core';
import { first } from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import {
  ApproveChangeOrderInput,
  CreateChangeOrderInput,
  EntityType,
  GqlService,
  listChangeOrdersQuery,
  NoteType,
  UpdateChangeOrderInput,
  WorkflowDetail,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { RequireSome, Utils } from '@shared/utils/utils';

import { ChangeOrderModel, ChangeOrderStore, ChangeOrderVerification } from './change-order.store';

@Injectable({ providedIn: 'root' })
export class ChangeOrderService {
  private changeOrderStore = inject(ChangeOrderStore);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  async add(changeOrder: CreateChangeOrderInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createChangeOrder$(changeOrder)
    );

    if (success && data) {
      this.changeOrderStore.add({
        ...data,
        ...this.workflowToVerifications(data.workflow_details),
        organization_id: data.organization.id,
      });
    }

    return { data, errors, success };
  }

  async approveChangeOrder({ id, organization_id }: ApproveChangeOrderInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.approveChangeOrder$({ id, organization_id })
    );

    if (success && data) {
      this.changeOrderStore.update(id, {
        ...data,
        organization_id,
      } as Partial<ChangeOrderModel>);
    }

    return { data, errors, success };
  }

  async createNote(entity_id: string, note_type: NoteType, message: string) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createNote$({
        entity_id,
        entity_type: EntityType.CHANGE_ORDER,
        message: Utils.scrubUserInput(message),
        note_type,
      })
    );

    if (success && data) {
      this.overlayService.success();
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.changeOrderStore.setLoading(true);

        return this.gqlService.listChangeOrders$();
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          data.forEach((changeOrder) => {
            if (changeOrder.reasons && changeOrder.reasons.length > 0) {
              changeOrder.decline_reason = Utils.unscrubUserInput(
                changeOrder.reasons.find(
                  (reason) => reason.note_type === NoteType.NOTE_TYPE_DECLINE_REASON
                )?.message
              );
            }
          });

          this.changeOrderStore.remove(() => true);
          this.changeOrderStore.set(
            data.map(
              (x) =>
                ({
                  ...x,
                  ...this.workflowToVerifications(x.workflow_details),
                  organization_id: x.organization.id,
                }) as unknown as ChangeOrderModel
            )
          );
        } else {
          this.overlayService.error(errors);
        }

        this.changeOrderStore.setLoading(false);
      })
    );
  }

  getOne(id: string) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.changeOrderStore.setLoading(true);

        return this.gqlService.getChangeOrder$(id);
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          if (data.reasons && data.reasons.length > 0) {
            data.decline_reason = Utils.unscrubUserInput(
              data.reasons.find((reason) => reason.note_type === NoteType.NOTE_TYPE_DECLINE_REASON)
                ?.message
            );
          }

          this.changeOrderStore.add({
            ...data,
            ...this.workflowToVerifications(data.workflow_details),
            organization_id: data.organization.id,
          });
        } else {
          this.overlayService.error(errors);
        }

        this.changeOrderStore.setLoading(false);
      })
    );
  }

  listChangeOrders() {
    return this.gqlService.listChangeOrderNos$().pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data : null };
      })
    );
  }

  async remove(changeOrder: ChangeOrderModel, delete_reason: string) {
    this.changeOrderStore.setLoading(true);

    if (delete_reason.length > 0) {
      const { data, errors, success } = await this.createNote(
        changeOrder.id,
        NoteType.NOTE_TYPE_DELETE_REASON,
        delete_reason
      );

      if (!success) {
        return { data, errors, success };
      }
    }

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.removeChangeOrder$({ id: changeOrder.id })
    );

    if (success && data) {
      this.changeOrderStore.remove(changeOrder.id);
      this.overlayService.success(`Change Order #${changeOrder.change_order_no} removed!`);
    } else {
      this.overlayService.error(errors);
    }

    this.changeOrderStore.setLoading(false);

    return { data, errors, success };
  }

  toChangeOrderModel = (listChangeOrdersQuery: listChangeOrdersQuery): ChangeOrderModel => {
    const declineReason =
      listChangeOrdersQuery.reasons && listChangeOrdersQuery.reasons.length > 0
        ? Utils.unscrubUserInput(
            listChangeOrdersQuery.reasons.find(
              (reason) => reason.note_type === NoteType.NOTE_TYPE_DECLINE_REASON
            )?.message
          )
        : listChangeOrdersQuery.decline_reason;

    return {
      ...listChangeOrdersQuery,
      ...this.workflowToVerifications(listChangeOrdersQuery.workflow_details),
      decline_reason: declineReason,
      organization_id: listChangeOrdersQuery.organization.id,
    } as unknown as ChangeOrderModel;
  };

  async update(input: UpdateChangeOrderInput) {
    if (
      (input.admin_review_reason && input.admin_review_reason.length > 0) ||
      (input.decline_reason && input.decline_reason.length > 0)
    ) {
      const { data, errors, success } = await this.createNote(
        input.id,
        input.admin_review_reason
          ? NoteType.NOTE_TYPE_ADMIN_REVIEW_REASON
          : NoteType.NOTE_TYPE_DECLINE_REASON,
        input.admin_review_reason ? input.admin_review_reason : input.decline_reason || ''
      );

      if (!success) {
        return { data, errors, success };
      }
    }

    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateChangeOrder$(input)
    );

    if (success && data) {
      this.changeOrderStore.update(input.id, () => ({
        ...data,
        decline_reason: input.decline_reason,
        organization_id: data.organization.id,
      }));
    }

    return { data, errors, success };
  }

  async verificationUpdate({
    co_id,
    verifications,
    workflow_id,
  }: {
    co_id: string;
    verifications: ChangeOrderVerification[];
    workflow_id: string;
  }) {
    const { errors, success } = await firstValueFrom(
      this.gqlService.updateWorkflowDetail$({
        id: workflow_id,
        properties: JSON.stringify({
          verifications,
        }),
      })
    );

    if (success) {
      this.changeOrderStore.update(co_id, {
        verifications: Utils.clone(verifications),
      });

      this.overlayService.success();
    } else {
      this.overlayService.error(errors);
    }
  }

  workflowToVerifications(workflows: RequireSome<Partial<WorkflowDetail>, 'id' | 'properties'>[]): {
    verifications: ChangeOrderVerification[];
    workflow_id: string;
  } {
    const workflow = first(workflows);

    if (!workflow) {
      return {
        verifications: [],
        workflow_id: '',
      };
    }

    const { verifications } = JSON.parse(workflow.properties || '') as {
      verifications: ChangeOrderVerification[];
    };

    return {
      verifications: verifications || [],
      workflow_id: workflow.id,
    };
  }
}
